import {
  useRef, useState, useCallback, useEffect, useMemo,
  FormEvent, RefObject,
} from 'react';
import { dequal } from 'dequal';
import {
  MirrorData, SetMirrorData, OptimisticJobTemplate, ExtendedJobTemplate,
} from '@type/job-add';
import { LexicalEditorType, LexicalEditorImperative } from '@type/lexical';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { route } from '@services/symfony';
import { useBjForm } from '@hooks/useBjForm';
import { useDebounce } from '@hooks/useDebounce/useDebounce';
import { showPhoneFlowModal, closePhoneFlowModal } from '@components/features/modals/phone/PhoneFlowModal';
import { useSwrJobTemplate } from '@hooks/useSwrApi/endpoints/job-operations/useSwrJobTemplate';
import { useSwrActivateJob } from '@hooks/useSwrApi/endpoints/publication_v2/useSwrActivateJob';
import { useSwrMissingInfo } from '@hooks/useSwrApi/endpoints/common/useSwrMissingInfo';
import { cleanObject, omitFromObject } from '@utils/objects';
import { getObjectFromSessionStorage } from '@utils/form-helpers/session-storage';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { useSwrGlobal } from '@hooks/useSwrApi';
import NiceModal from '@ebay/nice-modal-react';
import { RedirectCountdownModal } from '@components/features/modals/redirects/RedirectCountdownModal';
import { showAddBillingDataModal } from '@components/features/modals/billing-data/AddBillingDataModal';
import { useSwrCompanyOnboarding } from '@hooks/useSwrApi/endpoints/company/useSwrCompanyOnboarding';
import { useSwrCreateCompany } from '@hooks/useSwrApi/endpoints/company/useSwrCreateCompany';

/** Props */
interface UseEditJobFormProps {
  locale: Locale;
  jobSlug?: string;
  apiJobTemplate?: ApiSchema<'JobModel2'>,
  isAuth?: boolean,
  storageFormName?: string,
}

/**
 * @description Helper function for keeping only the good values from the job template (remove old/unneeded keys).
 * - `data` - the job template data
 */
const keepGoodValues = (data: Record<string, unknown> | object) => {
  const cleaned = omitFromObject(
    cleanObject(data as Record<string, unknown>),
    ['benefits', 'domainSeemsOk', 'locations'],
  );

  return cleaned;
};

/**
 * @description Main job-add hook.
 * - `locale` - the current locale
 * - `jobSlug` - the job slug
 * - `apiJobTemplate` - the job template data
 * - `isAuth` - whether the user is authenticated
 * - `storageFormName` - the name of the form in the session storage
 */
export const useEditJobForm = (props: UseEditJobFormProps) => {
  // Destructure props
  const {
    locale,
    jobSlug,
    apiJobTemplate,
    isAuth = false,
    storageFormName = 'job-add-form',
  } = props;

  // Router
  const {
    router,
    withLocale,
  } = useExtendedRouter();

  // FORM REFS
  // **************************
  const jobFormRef = useRef<HTMLFormElement>(null);

  // Job description
  const jobDescriptionRef = useRef<LexicalEditorType>(null);
  const jobDescriptionImperativeRef = useRef<LexicalEditorImperative>(null);
  const isEditorValid = () => jobDescriptionImperativeRef?.current?.checkLexicalValidity();


  // FORM STATES
  // **************************
  // Holds all information about the job. Use it to know when to trigger requests, modify the form, etc.
  const [mirrorData, mirrorDataSetter] = useState<MirrorData | undefined>(
    apiJobTemplate ? keepGoodValues(apiJobTemplate) : undefined,
  );

  // Holds the job slug for API requests
  const [currentJobSlug, setCurrentJobSlug] = useState<string | undefined>(jobSlug);
  // For draftMode when saving/activating the job
  const [saveJobOnly, setSaveJobOnly] = useState<boolean>(false);

  // We're going to use the global SWR to clear my jobs cache when the job is posted or saved.
  const { clearCacheKeysIncludes } = useSwrGlobal();

  // SWR: GET THE JOB TEMPLATE.
  // Usually triggered by a selection from the dropdown title.
  // **************************
  const {
    data: jobTemplate,
    mutate: mutateJobTemplate,
    isValidating: isJobTemplateLoading,
  } = useSwrJobTemplate({
    locale,
    params: { jobSlug: currentJobSlug },
    serverData: apiJobTemplate,
    successCallback: (data) => {
      // If we load a new job template, we update the mirror data state with the new values
      mirrorDataSetter((prev) => {
        const cleanedData = keepGoodValues(data as MirrorData);
        const areEqual = dequal(prev, cleanedData);
        return areEqual ? prev : cleanedData;
      });
    },
  });

  // Helper to update the SWR job template optimistically, without revalidating the cache (no actual fetching happens).
  // - update the job template with the new title: optimisticJobTemplate({ title: selectedItem?.name });
  // - soft reset the job template but also update the title: optimisticJobTemplate({ title: selectedItem?.name }, true);
  const optimisticJobTemplate: OptimisticJobTemplate = useCallback((newData, softReset) => {
    const finalData = softReset
      ? { ...apiJobTemplate, ...newData } as ExtendedJobTemplate
      : { ...jobTemplate, ...newData } as ExtendedJobTemplate;
    void mutateJobTemplate(finalData, {
      optimisticData: finalData,
      revalidate: false,
    });
  }, [apiJobTemplate, mutateJobTemplate, jobTemplate]);


  // USE BJ FORM
  // **************************
  const {
    record,
    checkFormValidation,
    formValues,
    setFormValues,
    getFieldValue,
    getFormData,
    handleCallbackErrors,
    updateFormElements,
  } = useBjForm({
    withDefaultValues: jobTemplate,
  });


  // UPDATE MIRROR DATA
  // **************************
  // Helper to set the mirror data state. It compares the previous state with the current data and updates the state
  // only if they are different.
  // - add the current form data to the mirror data: setMirrorData();
  // - same as above but overrides the title: setMirrorData({ title: 'New title' });
  // - resets the mirror data to the initial API data: setMirrorData(undefined, true);
  // - add only the new data to the mirror data: setMirrorData({ title: 'New title' }, false, true);
  const setMirrorData: SetMirrorData = useCallback((newData, reset, keepPrev) => {
    if (reset) {
      const currentValues = keepGoodValues(apiJobTemplate as MirrorData);
      return newData ? mirrorDataSetter({ ...currentValues, ...newData }) : mirrorDataSetter(currentValues);
    }

    const timedUpdate = setTimeout(() => {
      mirrorDataSetter((prev) => {
        // We need to check if the values are equal to avoid unnecessary re-renders
        const jobDescription = keepPrev ? prev?.description : jobDescriptionImperativeRef?.current?.getLexicalContent();
        const currentValues = keepPrev ? prev : getFormData(jobFormRef);
        let values = { ...currentValues, description: jobDescription } as Partial<ExtendedJobTemplate>;
        if (newData) values = { ...values, ...newData };
        const areEqual = dequal(prev, values);
        return areEqual ? prev : values;
      });
    }, 100);

    return () => clearTimeout(timedUpdate);
  }, [getFormData, apiJobTemplate]);


  // HANDLE JOB LANGUAGES
  // **************************
  const [selectedLanguages, setSelectedLanguages] = useState<ApiSchema<'SpokenLanguageWrite2'>[] | undefined>(jobTemplate?.spokenLanguages);

  useEffect(() => {
    if (selectedLanguages) {
      mirrorDataSetter((prev) => {
        const areEqual = dequal(prev?.spokenLanguages, selectedLanguages);
        return areEqual ? prev : { ...prev, spokenLanguages: selectedLanguages };
      });
    }
  }, [selectedLanguages]);


  // HANDLE JOB INTERVIEW QUESTIONS LIST
  // **************************
  const [interviewQuestionList, setInterviewQuestionList] = useState<ApiSchema<'InterviewQuestion'>[] | undefined>(jobTemplate?.interviewQuestions);

  useEffect(() => {
    if (!interviewQuestionList && jobTemplate?.interviewQuestions) {
      setInterviewQuestionList(jobTemplate.interviewQuestions);
    }
  }, [jobTemplate?.interviewQuestions, interviewQuestionList]);

  useEffect(() => {
    if (interviewQuestionList) {
      mirrorDataSetter((prev) => {
        const areEqual = dequal(prev?.interviewQuestions, interviewQuestionList);
        return areEqual ? prev : { ...prev, interviewQuestions: interviewQuestionList };
      });
    }
  }, [interviewQuestionList]);


  // HANDLE JOB REQUIREMENT QUESTIONS LIST
  // **************************
  const [requirementQuestionList, setRequirementQuestionList] = useState<ApiSchema<'InterviewQuestion'>[] | undefined>(jobTemplate?.requirementQuestions);

  useEffect(() => {
    if (!requirementQuestionList && jobTemplate?.requirementQuestions) {
      setRequirementQuestionList(jobTemplate.requirementQuestions);
    }
  }, [jobTemplate?.requirementQuestions, requirementQuestionList]);

  useEffect(() => {
    if (requirementQuestionList) {
      mirrorDataSetter((prev) => {
        const areEqual = dequal(prev?.requirementQuestions, requirementQuestionList);
        return areEqual ? prev : { ...prev, requirementQuestions: requirementQuestionList };
      });
    }
  }, [requirementQuestionList]);


  // SWR: CHECK MISSING INFO (new account posting job)
  // Used to check if the company has all the required information to post a job.
  // **************************
  const {
    data: missingInfo,
    mutate: getMissingInfo,
  } = useSwrMissingInfo({
    locale,
    shouldFetch: isAuth,
    type: 'business',
  });


  // SWR: SAVE/ACTIVATE JOB
  // **************************
  const {
    trigger: postJob,
    isMutating: isSubmitting,
  } = useSwrActivateJob({
    locale,
    jobSlug,
    draftMode: saveJobOnly,
    successCallback: (response) => {
      setSaveJobOnly(false);
      const postedJobSlug = response?.slug;
      const statusActivated = response?.status === 'ACTIVATED';
      const statusSaved = response?.status === 'SAVED';

      // Clear SWR Cache so when accessing my-jobs page next time it will fetch fresh data
      clearCacheKeysIncludes('/v1/my-jobs');

      // Trigger google tag manager event and redirect to the after-activation page
      if (statusActivated && postedJobSlug) {
        sendGoogleTagEvent('add-job-activated', 'custom', {
          status: response.status,
          slug: postedJobSlug,
          ...(response?.publicUrl && { publicUrl: response.publicUrl }),
          ...(response?.jobId && { jobId: response.jobId }),
        });
        if (response?.checkoutSessionUrl) {
          void NiceModal.show(RedirectCountdownModal, { url: response.checkoutSessionUrl, seconds: 5 });
          return;
        }
        router.push(`/after-activation/${postedJobSlug}`);
      } else if (statusSaved && postedJobSlug) {
        router.push(route('company_job_detail', { slug: postedJobSlug }), { withLocale: false });
      }
    },
    errorCallback: (error) => {
      setSaveJobOnly(false);
      handleCallbackErrors(error);
    },
  });


  // Debounce the post job function to avoid multiple requests and let the state enough time to update
  // **************************
  const postJobDebounced = useDebounce((postData: ApiSchema<'GeneralActivationData'>) => {
    void postJob(postData);
  });

  const { data: employerData } = useSwrCompanyOnboarding(locale);

  const { trigger: createCompany } = useSwrCreateCompany({
    locale,
    successCallback: () => {
      if (missingInfo?.fields?.includes('phoneConfirm')) {
        // Complete the phone confirmation if the phone is not confirmed
        showPhoneFlowModal({
          locale,
          startFromConfirm: true,
          successConfirmPhoneCallback: () => {
            void getMissingInfo();
            closePhoneFlowModal();
          },
        });
      } else {
        postJobDebounced(mirrorData as ApiSchema<'GeneralActivationData'>);
      }
    },
  });

  // **************************
  const handleMissingInfo = useCallback((postData: ApiSchema<'GeneralActivationData'>) => {
    if (missingInfo?.fields?.includes('profile') && employerData) {
      if (!mirrorData?.companyName && employerData?.firstName && employerData?.lastName && employerData?.phone?.number) {
        void createCompany({
          ...employerData,
          isIndividual: 1,
        });
      } else {
        showAddBillingDataModal(locale, employerData, () => {
          void getMissingInfo();
        }, !!mirrorData?.companyName);
      }
    } else if (missingInfo?.fields?.includes('phone')) {
      // Complete the full phone flow if the phone is missing
      showPhoneFlowModal({
        locale,
        successConfirmPhoneCallback: () => {
          void getMissingInfo();
          closePhoneFlowModal();
        },
      });
    } else if (missingInfo?.fields?.includes('phoneConfirm')) {
      // Complete the phone confirmation if the phone is not confirmed
      showPhoneFlowModal({
        locale,
        startFromConfirm: true,
        successConfirmPhoneCallback: () => {
          void getMissingInfo();
          closePhoneFlowModal();
        },
      });
    } else {
      // The user can post the job, the missing info is not blocking
      postJobDebounced(postData);
    }
  }, [locale, getMissingInfo, missingInfo?.fields, postJobDebounced, mirrorData?.companyName, employerData, createCompany]);


  // SUBMIT FORM
  // **************************
  // Helper to submit the form. It checks the form validation and scrolls to the editor if needed.
  // - activate job: submitForm(event);
  // - save the job (no activation): submitForm(event, false);
  const submitForm = useCallback(
    (event: FormEvent<HTMLFormElement> | RefObject<HTMLFormElement | null>, activateJob?: boolean) => {
    // Prevent the default form submission
      if (!('current' in event)) event?.preventDefault();
      if (activateJob === false) setSaveJobOnly(true);

      // Check the form validation
      const formData = checkFormValidation(event);
      const editorValid = isEditorValid();

      // If the form data is an object, it means the form is valid
      if (typeof formData === 'object') {
      // Scroll to editor if it's not valid but the form is
        if (!editorValid && jobDescriptionRef?.current) {
          const editorElement = jobDescriptionRef.current.getRootElement() as HTMLElement;
          editorElement.scrollIntoView({ behavior: 'smooth' });
        }

        // Build the post data
        const jobModel = omitFromObject(formData, ['packageId', 'repostingNumber', 'withoutCharge'] as keyof typeof formData) as ApiSchema<'JobModel3'>;
        const jobDescription = jobDescriptionImperativeRef?.current?.getLexicalContent();
        const locations = jobModel?.addresses ?? [];
        const postData: ApiSchema<'GeneralActivationData'> = {
          packageId: formData?.['packageId' as keyof typeof formData],
          repostingNumber: formData?.['repostingNumber' as keyof typeof formData],
          withoutCharge: formData?.['withoutCharge' as keyof typeof formData],
          jobModel: {
            ...jobModel,
            ...(selectedLanguages && { spokenLanguages: selectedLanguages }),
            ...(jobDescription && { description: jobDescription }),
            ...(interviewQuestionList && { interviewQuestions: interviewQuestionList }),
            ...(requirementQuestionList && { requirementQuestions: requirementQuestionList }),
            // this should not happen, since is not required, but API throws 500 without it...
            ...(locations && { locations }),
          },
        };

        // Post the job if the user is authenticated
        if (isAuth) {
          if (missingInfo && missingInfo?.fields) {
          // We check if the user has all the required information to post a job
            handleMissingInfo(postData);
          } else {
          // If the user has all the required information, we post the job
            postJobDebounced(postData);
          }
        } else {
        // Save the job data in the session storage
          sessionStorage.setItem(storageFormName, JSON.stringify(postData));
          // Redirect to the login page with the target path to the job edit page
          void router.push(`/login?targetPath=${encodeURIComponent(withLocale('/job-add/edit'))}`, { withLocale: false });
        }
      }
    },
    [
      checkFormValidation, handleMissingInfo, interviewQuestionList, isAuth,
      missingInfo, postJobDebounced, requirementQuestionList, router, selectedLanguages, storageFormName, withLocale,
    ],
  );


  // GET THE FULL FORM DATA WITHOUT VALIDATION
  // **************************
  const getJobFormData = useCallback(() => {
    const baseData = getFormData(jobFormRef) as ApiSchema<'JobModel3'>;
    const jobDescription = jobDescriptionImperativeRef?.current?.getLexicalContent();
    return {
      ...baseData,
      ...(selectedLanguages && { spokenLanguages: selectedLanguages }),
      ...(jobDescription && { description: jobDescription }),
      ...(interviewQuestionList && { interviewQuestions: interviewQuestionList }),
      ...(requirementQuestionList && { requirementQuestions: requirementQuestionList }),
    } as ApiSchema<'JobModel3'>;
  }, [getFormData, interviewQuestionList, requirementQuestionList, selectedLanguages]);


  // SHOW/HIDE JOB FORM & MORE OPTIONS
  // **************************
  const [showJobForm, setShowJobForm] = useState<boolean>(!!jobSlug);
  const [showMoreOptions, setShowMoreOptions] = useState<boolean>(false);

  useEffect(() => {
    // Match the minimum length for the job slug or title
    const hasSlug = !!(currentJobSlug && currentJobSlug.length >= 3);
    const hasTitle = !!(mirrorData?.title && mirrorData.title.length >= 3);
    setShowJobForm(hasSlug || hasTitle);
  }, [currentJobSlug, mirrorData?.title]);


  // UPDATE THE FORM WITH THE SESSION STORAGE VALUES IF THE USER WAS NOT AUTHENTICATED
  // **************************
  const updatedFromStorage = useRef<boolean>(false);
  const getStorageValue = useMemo(() => (typeof window !== 'undefined' ? getObjectFromSessionStorage(storageFormName, false, false) : {}), [storageFormName]);
  const storageValue = useMemo(
    () => (Object.keys(getStorageValue).length > 0 ? getStorageValue as ApiSchema<'GeneralActivationData'> : undefined),
    [getStorageValue],
  );

  useEffect(() => {
    if (storageValue && isAuth && !updatedFromStorage.current) {
      updatedFromStorage.current = true;
      const jobModel: ApiSchema<'JobModel3'> | undefined = storageValue?.jobModel as ApiSchema<'JobModel3'>;
      // Update the form values
      if (jobModel) {
        optimisticJobTemplate(jobModel);
        setMirrorData(jobModel);
        setShowJobForm(true);
      }
      // Cleaning
      sessionStorage.removeItem(storageFormName);
    }
  }, [storageValue, setMirrorData, storageFormName, isAuth, optimisticJobTemplate]);


  // Return
  // **************************
  return {
    // Job template  elements
    currentJobSlug,
    setCurrentJobSlug,
    jobTemplate,
    isJobTemplateLoading,
    optimisticJobTemplate,

    // Job description elements
    jobDescriptionRef,
    jobDescriptionImperativeRef,

    // Job languages elements
    selectedLanguages,
    setSelectedLanguages,

    // Interview questions elements
    interviewQuestionList,
    setInterviewQuestionList,

    // Requirement questions elements
    requirementQuestionList,
    setRequirementQuestionList,

    // BJ form elements
    jobFormRef,
    record,
    formValues,
    setFormValues,
    getFieldValue,
    updateFormElements,
    submitForm,
    isSubmitting,
    saveJobOnly,
    getJobFormData,


    // Form mirror data
    mirrorData,
    setMirrorData,

    // Show/hide the job form
    showJobForm,
    setShowJobForm,
    // Show more options
    showMoreOptions,
    setShowMoreOptions,
  };
};
